/**
 * Endpoints del POS.
 *
 * Mismo patron que `@/vue/constants/quick_booking.js` ({ path, method }), pero
 * declarado DENTRO del modulo porque esta entrega no toca archivos fuera de
 * `Modules/QuickBooking/Resources/`.
 *
 * Las rutas son RELATIVAS a propósito: la pantalla se sirve en `/quick-booking`,
 * así que `api/pos/catalog` resuelve a `/api/pos/catalog` (misma regla que ya
 * usa el flujo de reserva). No se antepone `/` para no romper instalaciones en
 * subdirectorio.
 */

// --- POS (Ola P1.1) -------------------------------------------------------
export const POS_CATALOG = ({ branch_id }) => {
  return { path: `api/pos/catalog?branch_id=${encodeURIComponent(branch_id)}`, method: 'GET' }
}

export const POS_CUSTOMER_SEARCH = ({ q }) => {
  return { path: `api/pos/customer-search?q=${encodeURIComponent(q)}`, method: 'GET' }
}

export const POS_CUSTOMER_STORE = () => {
  return { path: 'api/pos/customer', method: 'POST' }
}

export const POS_CUSTOMER_ADDRESSES = ({ user_id }) => {
  return { path: `api/pos/customer-addresses?user_id=${encodeURIComponent(user_id)}`, method: 'GET' }
}

export const POS_CUSTOMER_ADDRESS_STORE = () => {
  return { path: 'api/pos/customer-address', method: 'POST' }
}

// --- ORDEN DE TRABAJO (la cita en curso; no hay tabla de comandas) ---
export const POS_OPEN_ORDERS = ({ branch_id }) => {
  return { path: `api/pos/open-orders?branch_id=${encodeURIComponent(branch_id)}`, method: 'GET' }
}

export const POS_ORDER_DETAIL = (bookingId) => {
  return { path: `api/pos/order/${bookingId}`, method: 'GET' }
}

/** «Enviar a caja»: deja la venta como orden abierta, sin cobrar (2026-10-16). */
export const POS_SEND_ORDER = () => {
  return { path: 'api/pos/send-order', method: 'POST' }
}

export const POS_SALE = () => {
  return { path: 'api/pos/sale', method: 'POST' }
}

/** Datafast paso 1: crea el checkout (devuelve checkout_id + widget_url). */
export const POS_DATAFAST_CHECKOUT = () => {
  return { path: 'api/pos/payments/datafast/checkout', method: 'POST' }
}

// --- CAJA (CashRegisterController reutilizado bajo el prefijo pos) ---
export const POS_CASH_CURRENT = ({ branch_id }) => {
  return { path: `api/pos/cash/current?branch_id=${encodeURIComponent(branch_id)}`, method: 'GET' }
}

export const POS_CASH_OPEN = () => {
  return { path: 'api/pos/cash/open', method: 'POST' }
}

export const POS_CASH_MOVEMENT = (id) => {
  return { path: `api/pos/cash/${id}/movement`, method: 'POST' }
}

export const POS_CASH_CLOSE = (id) => {
  return { path: `api/pos/cash/${id}/close`, method: 'POST' }
}

/** Reporte X del dia (resumen de mostrador). `date` opcional (default hoy). */
export const POS_DAILY_REPORT = ({ branch_id, date }) => {
  const q = date ? `&date=${encodeURIComponent(date)}` : ''
  return { path: `api/pos/daily-report?branch_id=${encodeURIComponent(branch_id)}${q}`, method: 'GET' }
}

/**
 * Consulta cedula/RUC -> valida el formato y AUTOLLENA la razon social del
 * receptor (previene el error 69 del SRI). Fail-soft en backend: si el gateway
 * no responde, `razon_social` viene null y `valido` refleja solo el formato.
 * Respuesta: { valido, razon_social, mensaje, ... }. Bajo auth:sanctum (sesion).
 */
export const POS_CONSULTAR_IDENTIFICACION = () => {
  return { path: 'api/sri/consultar-identificacion', method: 'POST' }
}

// --- Reutilizados del flujo de reserva (solo lectura, ya existian) --------
export const POS_BRANCH_LIST = () => {
  return { path: 'api/quick-booking/branch-list', method: 'GET' }
}

export const POS_EMPLOYEE_LIST = ({ branch_id, service_id }) => {
  return {
    path: `api/quick-booking/employee-list?branch_id=${encodeURIComponent(branch_id)}&service_id=${encodeURIComponent(service_id)}`,
    method: 'GET'
  }
}

/**
 * RIDE (PDF) de un comprobante SRI ya emitido.
 * Ruta real: `backend.sri.ride` => /app/sri/{id}/ride (middleware `auth`).
 */
export const POS_RIDE_URL = (sriDocumentId) => `app/sri/${sriDocumentId}/ride`

/**
 * Recibo 80mm de un movimiento de caja (ingreso / egreso / venta en efectivo).
 * Ruta web por SESION (`backend.cash.movement_receipt`), igual que el RIDE: en
 * el navegador ya hay cookie, asi que no se usa la URL firmada que necesita la
 * app movil.
 */
export const POS_CASH_RECEIPT_URL = (movementId) => `app/cash/movement/${movementId}/receipt`

/**
 * Tipos de identificacion aceptados por `POST pos/customer`
 * (validacion real del backend: `in:04,05,06` + regla EcuadorianIdentification).
 */
export const IDENTIFICATION_TYPES = [
  { value: '05', label: 'pos.lbl_id_cedula' },
  { value: '04', label: 'pos.lbl_id_ruc' },
  { value: '06', label: 'pos.lbl_id_pasaporte' }
]

/**
 * Medios de pago ofrecidos en mostrador.
 *
 * `offline` = el backend lo liquida sin pasarela (PaymentGatewayManager::isOffline);
 * el cajero ya tiene el dinero. `drawer` = ademas entra efectivo fisico al cajon
 * (PosController::DRAWER_METHODS) y genera movimiento `sale` en la caja abierta.
 * Los online exigen `payment_reference` y pasan por verify() antes de cobrar.
 */
export const PAYMENT_METHODS = [
  { value: 'cash', label: 'pos.lbl_pay_cash', offline: true, drawer: true },
  // Tarjeta con datafono NO integrado: el cajero pasa la tarjeta en el aparato
  // y transcribe el voucher. `voucher: true` dispara los campos de captura.
  // Ojo: `datafast_voucher` es el flujo MANUAL; `datafast` (mas abajo) es la
  // pasarela online OPPWA, que exige referencia y verify() contra su API.
  { value: 'card_credit', label: 'pos.lbl_pay_card_credit', offline: true, drawer: false, voucher: true },
  { value: 'card_debit', label: 'pos.lbl_pay_card_debit', offline: true, drawer: false, voucher: true },
  { value: 'datafast_voucher', label: 'pos.lbl_pay_datafast_voucher', offline: true, drawer: false, voucher: true },
  { value: 'transfer', label: 'pos.lbl_pay_transfer', offline: true, drawer: false },
  { value: 'deposito', label: 'pos.lbl_pay_deposito', offline: true, drawer: false },
  { value: 'cheque', label: 'pos.lbl_pay_cheque', offline: true, drawer: false },
  { value: 'payphone', label: 'pos.lbl_pay_payphone', offline: false, drawer: false },
  { value: 'datafast', label: 'pos.lbl_pay_datafast', offline: false, drawer: false },
  { value: 'deuna', label: 'pos.lbl_pay_deuna', offline: false, drawer: false },
  { value: 'kushki', label: 'pos.lbl_pay_kushki', offline: false, drawer: false },
  { value: 'stripe', label: 'pos.lbl_pay_stripe', offline: false, drawer: false },
  { value: 'paypal', label: 'pos.lbl_pay_paypal', offline: false, drawer: false }
]
